/* Document <head> management for SEO + social share previews.
 *
 * The app is a multi-tenant SPA opened from a table QR (`?cn=<code>&t=<token>`),
 * so the meaningful title/description/image only exist once the venue data has
 * loaded client-side. This util recomputes the whole head from the current store
 * state + route and applies it idempotently. It is called from three places:
 *   - router.afterEach        → on every navigation (page label changes)
 *   - App.vue watcher         → when venue data arrives/changes (name, photo, …)
 *   - MenuSingle watcher      → when a category resolves (category-name title)
 *
 * NOTE: link scrapers (WhatsApp / Telegram / iMessage / Facebook) do NOT run JS,
 * so they only ever see the static defaults baked into public/index.html. Making
 * per-venue previews reach scrapers needs server-side injection of these tags by
 * the backend for `?cn=<code>` requests — deferred. What we do here upgrades the
 * head for the browser tab and for any JS-capable client. */

import { store } from '@/store/store'
import i18n from '@/i18n'

// Create or update a <meta> tag identified by one attribute (name= or property=).
// Managed tags are stamped data-head="1" so we only ever touch our own. A falsy
// content is skipped, leaving the static index.html fallback in place rather than
// blanking the tag.
function upsertMeta(attr, key, content) {
  if (!content) return
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    el.setAttribute('data-head', '1')
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

// Resolve a possibly-relative asset/url against the origin so scrapers and the
// share sheet get a fully-qualified src.
function absolute(url) {
  if (!url) return null
  if (/^https?:\/\//i.test(url)) return url
  try {
    return new URL(url, window.location.origin).href
  } catch (e) {
    return url
  }
}

function venue() {
  const data = store.getters['getCompanyData']
  return (data && data.locale) || null
}

// The clean, shareable venue URL (root + ?cn=<code>) — mirrors Welcome.shareMenu,
// so og:url is the canonical link rather than the current table-bound one.
function canonicalUrl() {
  const code = store.getters['getCompanyName']
  return code
    ? `${window.location.origin}/?cn=${encodeURIComponent(code)}`
    : window.location.href
}

// Category name for the `/menu/:id` page (which is a category, not a dish).
function categoryName(route) {
  const id = route && route.params && route.params.id
  if (!id) return null
  const popular = store.getters['menuStore/getPopular']
  if (popular && String(popular.id) === String(id)) return popular.name
  const menu = store.getters['menuStore/getMenu'] || []
  const cat = menu.find(c => String(c.id) === String(id))
  return (cat && cat.name) || null
}

// Localized secondary label for a page. null → the page is the venue's "home"
// menu and gets the "Venue — Menu" form instead of "Page — Venue".
function pageLabel(route) {
  if (!route) return null
  switch (route.name) {
    case 'menu-favorites': return i18n.t('pages.menu.favorites.title')
    case 'menu-single':    return categoryName(route)
    case 'bill':
    case 'payment':        return i18n.t('pages.cart.viewBill')
    case 'review':         return i18n.t('nav.review.title')
    case 'tip':            return i18n.t('pages.tip.title')
    case 'history':        return i18n.t('pages.cart.orderHistory')
    case 'result':         return i18n.t('pages.paymentResult.gratification')
    default:               return null   // welcome / menu / loading
  }
}

function buildTitle(route, venueName) {
  if (!venueName) return i18n.t('seo.appName')
  const label = pageLabel(route)
  return label
    ? `${label} — ${venueName}`
    : `${venueName} — ${i18n.t('pages.menu.title')}`
}

function buildDescription(v, venueName) {
  const welcome = v && (v.menu_welcome_text || '').trim()
  if (welcome) return welcome
  return venueName
    ? i18n.t('seo.venueDescription', { name: venueName })
    : i18n.t('seo.defaultDescription')
}

/**
 * Recompute and apply <title>, description, Open Graph / Twitter tags and
 * theme-color from the current store state + route. Idempotent.
 * @param {import('vue-router').Route} route  current route (for the page label)
 */
export function applyHead(route) {
  const v = venue()
  const venueName = v && v.name
  const title = buildTitle(route, venueName)
  const description = buildDescription(v, venueName)
  const image = absolute(v && (v.photo || v.logo))

  document.title = title
  upsertMeta('name', 'description', description)

  // Open Graph — drives link previews.
  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', description)
  upsertMeta('property', 'og:url', canonicalUrl())
  upsertMeta('property', 'og:image', image)

  // Twitter mirrors OG; a real venue photo upgrades the card to the large format.
  upsertMeta('name', 'twitter:title', title)
  upsertMeta('name', 'twitter:description', description)
  upsertMeta('name', 'twitter:image', image)
  if (image) upsertMeta('name', 'twitter:card', 'summary_large_image')

  // theme-color (browser chrome tint) is owned by utils/theme.js, which keeps it
  // equal to --surface-page per light/dark theme. Don't override it with the
  // venue brand colour — a chrome that doesn't match the page background reads
  // as alien grey/coloured bars above and below the app on iOS.
}

export default applyHead
